// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { AdminPermissions, OrgPermissions, ProfileOutput, PermissionOutput } from 'thehive-sdk';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import Form from 'antd/es/form';
import { PermissionManager } from './PermissionManager';

export interface ProfileValuesForm {
    name: string;
    permissions: AdminPermissions[] | OrgPermissions[];
    isAdmin: boolean;
}

interface ProfileFormProps {
    form: FormInstance;
    defaultValues?: ProfileOutput;
    isValid: boolean;
    permissionList: PermissionOutput[];
    onFinish(values: ProfileValuesForm): void;
    onChange: () => void;
    onCancel: () => void;
}

export const ProfileForm = ({
    form,
    defaultValues,
    isValid,
    permissionList,
    onFinish,
    onChange,
    onCancel,
}: ProfileFormProps): ReactElement => {
    const i = useLocalMessage();

    const { forAdmin: isAdmin = true } = defaultValues || {};

    const formIsAdmin = Form.useWatch<boolean>(['isAdmin'], form);

    const onValuesChange = (): void => {
        onChange();
    };

    const orgPermissions = useMemo(
        () => permissionList.filter(({ scope }) => scope.includes('organisation')),
        [permissionList],
    );

    const adminPermissions = useMemo(
        () => permissionList.filter(({ scope }) => scope.includes('admin')),
        [permissionList],
    );

    const permissions = formIsAdmin ? adminPermissions : orgPermissions;

    const tradLabelKey = !!defaultValues ? 'edit' : 'create';

    return (
        <THForm<ProfileValuesForm>
            name="profile-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onValuesChange}
            initialValues={{ isAdmin, ...defaultValues }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        disabled={!isValid}
                        footerText={i(`profile.${tradLabelKey}.form.submit`)}
                    />
                }
            >
                <THForm.Item
                    label={i('profile.create.form.name.label')}
                    name="name"
                    rules={[{ required: true, message: i('profile.create.form.name.required') }]}
                >
                    <THInput placeholder={i('profile.create.form.name.placeholder')} disabled={!!defaultValues} />
                </THForm.Item>

                <THForm.Item name="isAdmin" label={i('profile.create.form.is-admin.label')}>
                    <THRadio.Group>
                        <THRadio value={true}>{i('profile.create.form.is-admin.admin')}</THRadio>
                        <THRadio value={false}>{i('profile.create.form.is-admin.org')}</THRadio>
                    </THRadio.Group>
                </THForm.Item>

                <THForm.Item<ProfileValuesForm> noStyle shouldUpdate={(prev, curr) => prev.isAdmin !== curr.isAdmin}>
                    <THForm.Item name="permissions" label={i('profile.create.form.permissions.label')}>
                        <PermissionManager permissions={permissions} isAdmin={formIsAdmin} />
                    </THForm.Item>
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
